// Decodifica un VIN con la API pública de la NHTSA (vPIC): gratis, sin key y
// con CORS abierto, así que se llama desde el navegador. El cruce con nuestros
// vehículos lo hace vehicleFromVin (utils/vehicleIntent.js).
//
// Cubre los VIN del mercado de EE. UU. (Isuzu NPR/NQR/NRR, GM W-series...).
// Uno que no conozca vuelve sin marca/modelo/año: la página lo dice en vez de
// inventar un vehículo.

const VPIC_URL = "https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues";

// 17 caracteres, sin I, O ni Q (no existen en un VIN), con letras y cifras.
const VIN = /^[A-HJ-NPR-Z0-9]{17}$/;

export const isVin = word => {
  const text = String(word || "").toUpperCase();
  return VIN.test(text) && /[A-Z]/.test(text) && /\d/.test(text);
};

// Los últimos 8 de un VIN (posiciones 10 a 17: año, planta y serie), que es lo
// que suelen dar los clientes de camiones ("27003041"). El año no usa 0, U ni
// Z; la serie de estos fabricantes es numérica.
const VIN_TAIL = /^[1-9A-HJ-NPR-TV-Y][A-HJ-NPR-Z0-9]\d{6}$/;

export const isVinTail = word =>
  VIN_TAIL.test(
    String(word || "")
      .trim()
      .toUpperCase()
  );

// Posición 10: A = 1980 ... Y = 2000, 1 = 2001 ... 9 = 2009, y el ciclo se
// repite cada 30 años (A = 2010...).
const YEAR_CODES = "ABCDEFGHJKLMNPRSTVWXY123456789";

/**
 * Años posibles de un VIN (completo o sus últimos 8), el más reciente primero.
 * Para camiones la posición 7 no desempata el ciclo, así que "V" es 1997 o
 * 2027; los que aún no existen se descartan.
 */
export const vinYears = vin => {
  const text = String(vin || "").toUpperCase();
  const code = text.length === 17 ? text[9] : text[0];
  const at = YEAR_CODES.indexOf(code);
  if (at === -1) return [];
  const latest = new Date().getFullYear() + 1;
  return [2040, 2010, 1980]
    .map(base => base + at)
    .filter(year => year <= latest);
};

// Dígito de control (posición 9), obligatorio en los VIN de Norteamérica.
// prettier-ignore
const VIN_VALUES = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Si el dígito de control cuadra. Entre dos VIN con el mismo final
 * (JALC4814917000407 y JALC4B14917000407: el mismo camión mal escrito) dice
 * cuál es el bueno.
 */
export const vinCheckDigitOk = vin => {
  const text = String(vin || "").toUpperCase();
  if (!VIN.test(text)) return false;
  const sum = [...text].reduce((total, char, i) => {
    const value = /\d/.test(char) ? Number(char) : VIN_VALUES[char];
    return total + value * VIN_WEIGHTS[i];
  }, 0);
  const remainder = sum % 11;
  return text[8] === (remainder === 10 ? "X" : String(remainder));
};

// Un VIN decodifica siempre igual: se guarda para toda la sesión.
const cache = new Map();

const clean = value => {
  const text = String(value === null || value === undefined ? "" : value);
  return text.trim() && text.trim() !== "Not Applicable" ? text.trim() : null;
};

/**
 * { vin, make, model, year, engine, liters, body, ok, note }
 * `ok`: la NHTSA dio marca, modelo y año (lo mínimo para cruzar). `note`: su
 * aviso si el VIN tiene algo raro (p. ej. el dígito de control).
 */
export const decodeVin = vin => {
  const key = String(vin).toUpperCase();
  if (!cache.has(key)) {
    const request = fetch(`${VPIC_URL}/${key}?format=json`)
      .then(response => {
        if (!response.ok) throw new Error(`NHTSA ${response.status}`);
        return response.json();
      })
      .then(body => {
        const row = (body.Results || [])[0] || {};
        const year = Number(row.ModelYear) || null;
        const decoded = {
          vin: key,
          make: clean(row.Make),
          model: clean(row.Model),
          year,
          engine: clean(row.EngineModel),
          liters: Number(row.DisplacementL) || null,
          body: clean(row.BodyClass),
          note:
            row.ErrorCode && row.ErrorCode !== "0" ? clean(row.ErrorText) : null
        };
        decoded.ok = Boolean(decoded.make && decoded.model && decoded.year);
        return decoded;
      })
      .catch(error => {
        // Un fallo de red no se queda en caché: se reintenta la próxima vez.
        cache.delete(key);
        throw error;
      });
    cache.set(key, request);
  }
  return cache.get(key);
};

/** "ISUZU NPR 2006 · 4HK1-TC 5.2L" */
export const decodedLabel = decoded =>
  [
    [decoded.make, decoded.model, decoded.year].filter(Boolean).join(" "),
    [decoded.engine, decoded.liters ? `${decoded.liters.toFixed(1)}L` : null]
      .filter(Boolean)
      .join(" ")
  ]
    .filter(Boolean)
    .join(" · ");
